import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getButtonClass, getCardClass } from '../lib/tailwindComponents';
import { useAuth } from '../contexts/Auth0Context.jsx';

const AuthError = () => {
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [errorDetails, setErrorDetails] = useState({
    reason: '',
    message: '',
    title: 'Authentication Error',
    description: 'There was a problem with your authentication.',
    icon: '🚫'
  });

  useEffect(() => {
    const reason = searchParams.get('reason') || 'unknown';
    const message = searchParams.get('message') || 'An unknown error occurred';

    let details = {
      reason,
      message,
      title: 'Authentication Error',
      description: message,
      icon: '🚫'
    };

    // Customize error messages based on reason
    switch (reason) {
      case 'email_not_verified':
        details = {
          ...details,
          title: 'Please Verify Your Email',
          description: 'We sent a verification link to your email address. Click the link in that email, then come back here and click the button below.',
          icon: '📧',
          showEmailVerificationHelp: true
        };
        break;
      case 'not_authorized':
        details = {
          ...details,
          title: 'Access Not Authorized',
          description: 'Your email address is not authorized to access this system. Please contact an administrator to request access.',
          icon: '🔒'
        };
        break;
      case 'invalid_token':
        details = {
          ...details,
          title: 'Invalid Authentication',
          description: 'Your authentication token is invalid or has expired. Please try logging in again.',
          icon: '⏰'
        };
        break;
      case 'oauth_error':
        details = {
          ...details,
          title: 'Sign Up Failed',
          description: 'There was a problem creating your account or signing in.',
          icon: '⚠️',
          showOAuthHelp: true
        };
        break;
      case 'network_error':
        details = {
          ...details,
          title: 'Network Error',
          description: 'Unable to connect to the authentication service. Please check your connection and try again.',
          icon: '🌐'
        };
        break;
      case 'backend_unavailable':
        details = {
          ...details,
          title: 'Server Starting Up',
          description: 'The backend is still waking up. Please wait a moment and retry the connection.',
          icon: '⏳'
        };
        break;
      default:
        details = {
          ...details,
          title: 'Authentication Error',
          description: message || 'An unexpected error occurred during authentication.',
          icon: '❌'
        };
    }

    setErrorDetails(details);
  }, [searchParams]);

  // For auth errors, we need to force a fresh login (not use cached session)
  const handleLoginAgain = () => {
    if (errorDetails.reason === 'backend_unavailable') {
      window.location.assign('/');
      return;
    }

    const requiresFreshLogin = ['email_not_verified', 'not_authorized', 'invalid_token'].includes(errorDetails.reason);

    if (requiresFreshLogin) {
      // Force Auth0 to show login screen, ignoring any existing session.
      // In dev bypass mode this is a no-op and does not crash.
      login({
        authorizationParams: {
          prompt: 'login'  // Forces fresh login, won't auto-authenticate
        }
      });
    } else {
      // For other errors, normal login flow is fine.
      login();
    }
  };

  // For email verification, just redirect through Auth0 normally
  // Auth0 will issue a fresh token with updated email_verified claim
  const handleRetryAfterVerification = () => {
    login();
  };

  return (
    <div className="min-h-screen bg-gaia-bg flex items-center justify-center p-4">
      <div className={`${getCardClass()} max-w-lg w-full p-8`}>
        {/* Icon */}
        <div className="text-6xl text-center mb-6">
          {errorDetails.icon}
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-white text-center mb-4">
          {errorDetails.title}
        </h1>

        {/* Description */}
        <p className="text-gaia-muted text-center mb-6 leading-relaxed">
          {errorDetails.description}
        </p>

        {/* Email verification help */}
        {errorDetails.showEmailVerificationHelp && (
          <div className="bg-gaia-bg/50 border border-gaia-border rounded-lg p-4 mb-6">
            <p className="text-sm text-gaia-muted mb-3">
              <span className="text-white font-medium">Can't find the email?</span>
            </p>
            <ul className="text-sm text-gaia-muted space-y-1 list-disc list-inside">
              <li>Check your spam or junk folder</li>
              <li>The email is from Auth0 or noreply@...</li>
              <li>It may take a few minutes to arrive</li>
            </ul>
          </div>
        )}

        {/* OAuth/signup error help */}
        {errorDetails.showOAuthHelp && (
          <div className="bg-gaia-bg/50 border border-gaia-border rounded-lg p-4 mb-6">
            <p className="text-sm text-gaia-muted mb-3">
              <span className="text-white font-medium">Things to try:</span>
            </p>
            <ul className="text-sm text-gaia-muted space-y-1 list-disc list-inside">
              <li>Clear your browser cookies and try again</li>
              <li>Try using an incognito/private window</li>
              <li>If signing up, the email may already be registered - try logging in instead</li>
              <li>Try a different browser</li>
            </ul>
          </div>
        )}

        {/* Error Details (if different from description and not a special help case) */}
        {errorDetails.message && errorDetails.message !== errorDetails.description && !errorDetails.showEmailVerificationHelp && !errorDetails.showOAuthHelp && (
          <div className="bg-gaia-bg/50 border border-gaia-border rounded-lg p-4 mb-6">
            <p className="text-xs text-gaia-muted mb-1">Error Message:</p>
            <p className="text-sm text-white">{errorDetails.message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {errorDetails.reason === 'email_not_verified' && (
            <>
              <button
                onClick={handleRetryAfterVerification}
                className={`${getButtonClass('primary')} w-full text-center`}
              >
                I've Verified My Email - Continue
              </button>
              <button
                onClick={handleLoginAgain}
                className={`${getButtonClass('secondary')} w-full text-center`}
              >
                Use Different Account
              </button>
            </>
          )}

          {errorDetails.reason === 'backend_unavailable' && (
            <button
              onClick={handleLoginAgain}
              className={`${getButtonClass('primary')} w-full text-center`}
            >
              Retry Connection
            </button>
          )}

          {errorDetails.reason !== 'email_not_verified' && errorDetails.reason !== 'backend_unavailable' && (
            <button
              onClick={handleLoginAgain}
              className={`${getButtonClass('primary')} w-full text-center`}
            >
              Try Again with Different Account
            </button>
          )}

          {errorDetails.reason === 'not_authorized' && (
            <div className="text-center">
              <p className="text-sm text-gaia-muted mb-2">
                Need access? Contact your administrator
              </p>
              <a
                href="mailto:admin@example.com?subject=Gaia Access Request"
                className={`${getButtonClass('secondary')} inline-block`}
              >
                Request Access
              </a>
            </div>
          )}
        </div>

        {/* Debug Info (only in development) */}
        {import.meta.env.DEV && (
          <details className="mt-8 text-xs text-gaia-muted">
            <summary className="cursor-pointer hover:text-white">Debug Information</summary>
            <pre className="mt-2 p-2 bg-gaia-bg rounded overflow-x-auto">
              {JSON.stringify({ 
                reason: errorDetails.reason, 
                message: errorDetails.message,
                params: Object.fromEntries(searchParams.entries())
              }, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default AuthError;
